import { ChangeDetectionStrategy, Component, computed, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { AutocompleteComponent, SubmitGroupComponent, TextComponent } from '@libs/form/components';
import { AutocompleteInputModel, SubmitGroupItemModel, TextInputModel } from '@libs/form/models';
import { CustomerModel } from '@libs/account/models';
import { AuthToken, JwtHelper } from '@libs/shared/models';
import { firstValueFrom } from 'rxjs';
import { DebuggerAuthState } from '../debugger.model';
import { customerNumberConfig, submitConfig, userTokenConfig } from './auth-debugger.model';
import { AuthDebuggerService } from './auth-debugger.service';

@Component({
  selector: 'ui-auth-debugger',
  styleUrls: ['./auth-debugger.component.scss'],
  templateUrl: './auth-debugger.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    SubmitGroupComponent,
    TextComponent,
    AutocompleteComponent,
    ReactiveFormsModule,
  ],
})
export class AuthDebuggerComponent {
  public readonly form: FormGroup<Record<'userToken' | 'customerNumber', FormControl>>;
  public readonly authState: Signal<DebuggerAuthState | undefined>;
  public readonly customers: Signal<CustomerModel[] | undefined>;

  public readonly userTokenConfig: Signal<TextInputModel>;
  public readonly customerNumberConfig: Signal<AutocompleteInputModel>;
  public readonly submitConfig: SubmitGroupItemModel = submitConfig;

  public constructor(
    private readonly service: AuthDebuggerService,
  ) {
    this.authState = toSignal(this.service.getAuthState());
    this.customers = toSignal(this.service.getCustomers());

    this.userTokenConfig = computed(() => ({
      ...userTokenConfig,
      hint: `User ID: ${this.authState()?.token?.userId}`,
      value: this.authState()?.token?.bearer ?? '',
    }));

    this.customerNumberConfig = computed(() => ({
      ...customerNumberConfig,
      value: this.authState()?.customerNumber,
      options: (this.customers() ?? []).map(
        customer => ({ label: `${customer.id} - ${customer.name}`, value: customer.id }),
      ),
    }));

    this.form = new FormGroup({
      userToken: new FormControl<string>('', { nonNullable: true }),
      customerNumber: new FormControl<string>('', { nonNullable: true }),
    });
  }

  public async onSubmit(): Promise<void> {
    const token: AuthToken | undefined = this.parseAuthToken(String(this.form.value.userToken));
    const customerNumber: string = String(this.form.value.customerNumber);

    await firstValueFrom(
      this.service.updateAuthState({ token, customerNumber }),
    );
  }

  private parseAuthToken(bearer: string): AuthToken | undefined {
    try {
      return bearer ? JwtHelper.parseJwt(bearer) : undefined;
    } catch {
      return undefined;
    }
  }
}
